import React from "react";
import { QUEST_PROGRESS_TABLE_GRID_COLUMNS } from "common/constants";
import type { AdminCompanyQuestProgressItem } from "common/types/questManagement.type";

interface QuestProgressTableProps {
  rows: AdminCompanyQuestProgressItem[];
  isLoading: boolean;
  errorMessage: string;
  onRetry: () => void;
  onDetailClick: (row: AdminCompanyQuestProgressItem) => void;
}

const QuestProgressTable = ({
  rows,
  isLoading,
  errorMessage,
  onRetry,
  onDetailClick,
}: QuestProgressTableProps) => {
  const renderRows = () => {
    if (isLoading) {
      return (
        <div className="flex min-h-[120px] items-center justify-center border-x border-b border-[#d5d5d5] text-[13px] text-[#1b4b86]">
          ユーザー進捗データを読み込み中です。
        </div>
      );
    }

    if (errorMessage) {
      return (
        <div className="flex min-h-[120px] flex-col items-center justify-center gap-[10px] border-x border-b border-[#d5d5d5] text-center">
          <p className="text-[13px] font-bold text-[#1b4b86]">{errorMessage}</p>
          <button
            className="h-[30px] rounded-[6px] bg-[#1b4b86] px-[15px] text-[12px] font-bold text-white"
            onClick={onRetry}
            type="button"
          >
            再読み込み
          </button>
        </div>
      );
    }

    if (rows.length === 0) {
      return (
        <div className="flex min-h-[120px] flex-col items-center justify-center gap-[8px] border-x border-b border-[#d5d5d5] text-center">
          <p className="text-[16px] font-bold text-[#1b4b86]">
            ユーザー進捗データがありません
          </p>
          <p className="text-[13px] text-black">
            現在、表示できるユーザー進捗データがありません。
          </p>
        </div>
      );
    }

    return rows.map((row, index) => {
      const progress = Math.max(0, Math.min(100, row.progress_percentage));

      return (
        <div
          key={row.company_id}
          className={`grid min-h-[41px] ${QUEST_PROGRESS_TABLE_GRID_COLUMNS} text-[13px] leading-[1.6] text-black ${
            index % 2 === 1 ? "bg-[#f9fcfe]" : "bg-white"
          }`}
        >
          <div className="border-x border-b border-[#d5d5d5] px-[15px] py-[10px]">
            {row.company_name || row.company_code || "-"}
          </div>
          <div className="flex h-full items-center border-b border-r border-[#d5d5d5] px-[15px] py-[10px]">
            <div className="h-[6px] w-[400px] overflow-hidden rounded-[3px] bg-[#eef5fa]">
              <div
                className="h-full rounded-[3px] bg-[linear-gradient(90deg,#d9ff43_0%,#30d9e7_100%)]"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
          <div className="flex h-full items-center justify-center border-b border-r border-[#d5d5d5] px-[15px] py-[10px]">
            <button
              className="font-['Noto_Sans_JP'] text-[13px] leading-[1.6] text-[#1b4b86] underline"
              onClick={() => onDetailClick(row)}
              type="button"
            >
              詳細表示
            </button>
          </div>
        </div>
      );
    });
  };

  const renderMobileCards = () => {
    if (isLoading) {
      return (
        <div className="flex min-h-[120px] items-center justify-center rounded-md border border-[#d5d5d5] text-[13px] text-[#1b4b86]">
          ユーザー進捗データを読み込み中です。
        </div>
      );
    }

    if (errorMessage) {
      return (
        <div className="flex min-h-[120px] flex-col items-center justify-center gap-2.5 rounded-md border border-[#d5d5d5] px-4 text-center">
          <p className="text-[13px] font-bold text-[#1b4b86]">{errorMessage}</p>
          <button
            className="h-[30px] rounded-md bg-[#1b4b86] px-[15px] text-xs font-bold text-white"
            onClick={onRetry}
            type="button"
          >
            再読み込み
          </button>
        </div>
      );
    }

    if (rows.length === 0) {
      return (
        <div className="flex min-h-[120px] flex-col items-center justify-center gap-2 rounded-md border border-[#d5d5d5] px-4 text-center">
          <p className="text-base font-bold text-[#1b4b86]">
            ユーザー進捗データがありません
          </p>
          <p className="text-[13px] text-black">
            現在、表示できるユーザー進捗データがありません。
          </p>
        </div>
      );
    }

    return rows.map((row) => {
      const progress = Math.max(0, Math.min(100, row.progress_percentage));

      return (
        <article
          key={row.company_id}
          className="overflow-hidden rounded-md border border-[#d5d5d5] bg-white"
        >
          <h3 className="flex min-h-[42px] items-center justify-center bg-[#1b4b86] px-4 py-2 text-center text-sm font-bold leading-[1.6] text-white">
            {row.company_name || row.company_code || "-"}
          </h3>
          <div className="p-5">
            <p className="text-sm leading-[1.6] text-[#adbbcc]">現在の進捗</p>
            <div className="mt-2 h-1.5 w-full overflow-hidden rounded bg-[#eef5fa]">
              <div
                className="h-full rounded bg-[linear-gradient(90deg,#d9ff43_0%,#30d9e7_100%)]"
                style={{ width: `${progress}%` }}
              />
            </div>
            <button
              className="mt-5 flex h-[42px] w-full items-center justify-center rounded-md bg-[#eef5fa] font-['Noto_Sans_JP'] text-sm font-bold text-[#1b4b86]"
              onClick={() => onDetailClick(row)}
              type="button"
            >
              詳細表示
            </button>
          </div>
        </article>
      );
    });
  };

  return (
    <div className="mt-[15px] sm:mt-5">
      <div className="grid gap-[15px] sm:hidden">{renderMobileCards()}</div>

      <div className="hidden overflow-x-auto sm:block">
        <div className="w-full min-w-[1110px]">
          <div
            className={`grid h-[42px] ${QUEST_PROGRESS_TABLE_GRID_COLUMNS} overflow-hidden rounded-t-[6px] bg-[#1b4b86] text-[14px] font-bold leading-[1.6] text-white`}
          >
            <div className="border-r border-white/30 px-[15px] py-[10px] text-center">
              ユーザー名
            </div>
            <div className="border-r border-white/30 px-[15px] py-[10px] text-center">
              現在の進捗
            </div>
            <div className="px-[15px] py-[10px] text-center">ログ管理</div>
          </div>
          {renderRows()}
        </div>
      </div>
    </div>
  );
};

export default QuestProgressTable;
